import { useState } from "react"
import { ChevronRight, Inbox, Linkedin } from "lucide-react"
import { DataGrid, type DataGridColumn } from "@/components/ui/DataGrid"
import { useOutreachEvents } from "@/hooks/useOutreach"
import { EVENT_TYPE_CONFIG } from "@/types/outreach"
import type { OutreachEvent, OutreachEventType } from "@/types/outreach"

// ── Badge helpers ──

function EventTypeBadge({ type }: { type: string }) {
  const cfg = EVENT_TYPE_CONFIG[type as OutreachEventType]
  const label = cfg?.title ?? type
  const color = cfg?.color ?? "#6b7280"
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: `${color}20`, color }}
    >
      {cfg?.icon && <cfg.icon className="w-3 h-3" />}
      {label}
    </span>
  )
}

function ChannelBadge({ channel }: { channel: string }) {
  const colors: Record<string, string> = {
    linkedin: "#0a66c2",
    email: "#3b82f6",
    whatsapp: "#25d366",
    phone: "#f59e0b",
  }
  const c = colors[channel?.toLowerCase()] ?? "#6b7280"
  return (
    <span
      className="inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize"
      style={{ backgroundColor: `${c}20`, color: c }}
    >
      {channel || "\u2014"}
    </span>
  )
}

function DirectionLabel({ direction }: { direction: string }) {
  return direction === "inbound" ? (
    <span className="text-green-400 text-xs font-medium">Entrada</span>
  ) : (
    <span className="text-blue-400 text-xs font-medium">Saida</span>
  )
}

function formatDate(iso: string) {
  if (!iso) return "\u2014"
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

// ── Contact cell with LinkedIn icon ──

function ContactCell({ row }: { row: OutreachEvent }) {
  // Use enriched contact_name from API, fallback to metadata
  const name = row.contact_name
    ?? (row.metadata as Record<string, string> | null)?.contact_name
    ?? null
  const linkedinUrl = row.contact_linkedin_url ?? null

  if (!name && !linkedinUrl) return <span className="text-muted-foreground">{"\u2014"}</span>

  return (
    <span className="inline-flex items-center gap-1.5">
      {linkedinUrl && (
        <a
          href={linkedinUrl.startsWith("http") ? linkedinUrl : `https://${linkedinUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center hover:opacity-80"
          style={{ backgroundColor: "#0A66C2" }}
          title="Abrir perfil LinkedIn"
        >
          <Linkedin className="w-3 h-3 text-white" />
        </a>
      )}
      <span className="truncate">{name || "\u2014"}</span>
    </span>
  )
}

// ── Columns ──

const columns: DataGridColumn<OutreachEvent>[] = [
  {
    id: "event_date",
    header: "Data",
    accessor: (row) => formatDate(row.created_at),
    sortValue: (row) => row.created_at ?? "",
    sortable: true,
    minWidth: "140px",
  },
  {
    id: "event_type",
    header: "Tipo",
    accessor: (row) => <EventTypeBadge type={row.event_type} />,
    filterValue: (row) => EVENT_TYPE_CONFIG[row.event_type]?.title ?? row.event_type,
    sortable: true,
    filterable: true,
    minWidth: "160px",
  },
  {
    id: "contact_name",
    header: "Contato",
    accessor: (row) => <ContactCell row={row} />,
    filterValue: (row) =>
      row.contact_name
      ?? (row.metadata as Record<string, string> | null)?.contact_name
      ?? "",
    sortable: true,
    filterable: true,
  },
  {
    id: "lead_title",
    header: "Lead",
    accessor: (row) => {
      const title = row.lead_title || row.lead_company || null
      return title ? (
        <span className="truncate text-xs">{title}</span>
      ) : (
        <span className="text-muted-foreground">{"\u2014"}</span>
      )
    },
    filterValue: (row) => row.lead_title ?? row.lead_company ?? "",
    sortable: true,
    filterable: true,
    minWidth: "140px",
  },
  {
    id: "subject",
    header: "Assunto",
    accessor: (row) => row.subject || "\u2014",
    filterValue: (row) => row.subject ?? "",
    sortable: true,
    filterable: true,
  },
  {
    id: "channel",
    header: "Canal",
    accessor: (row) => <ChannelBadge channel={row.channel} />,
    filterValue: (row) => row.channel ?? "",
    sortable: true,
    filterable: true,
    minWidth: "100px",
  },
  {
    id: "direction",
    header: "Direcao",
    accessor: (row) => <DirectionLabel direction={row.direction} />,
    filterValue: (row) => (row.direction === "inbound" ? "Entrada" : "Saida"),
    sortable: true,
    filterable: true,
    minWidth: "100px",
  },
]

// ── Main component ──

export default function OutreachEventsGrid({ onRowClick }: { onRowClick?: (event: OutreachEvent) => void }) {
  const [expanded, setExpanded] = useState(false)
  const { data, isLoading } = useOutreachEvents({ limit: 200 })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <div>
      {/* Accordion header */}
      <div
        className="flex items-center justify-between p-4 bg-card border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <ChevronRight
            className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
              expanded ? "rotate-90" : ""
            }`}
          />
          <h3 className="text-lg font-semibold">Emails LinkedIn Capturados</h3>
          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
            {total} emails
          </span>
        </div>
        <span className="text-sm text-muted-foreground hidden sm:inline">
          {expanded ? "Clique para recolher" : "Clique para expandir"}
        </span>
      </div>

      {/* Expandable body */}
      {expanded && (
        <div className="mt-2 border rounded-lg overflow-hidden">
          <DataGrid<OutreachEvent>
            gridId="outreach-events"
            columns={columns}
            data={items}
            isLoading={isLoading}
            emptyMessage="Nenhum evento capturado ainda"
            emptyIcon={<Inbox className="w-8 h-8 text-muted-foreground" />}
            onRowClick={onRowClick}
            pageSize={15}
          />
        </div>
      )}
    </div>
  )
}
